/**
 * Live TV Video Player
 * Handles HLS video streaming with HLS.js fallback for non-Safari browsers
 * Includes retry logic, error recovery, and network reconnection handling
 */

/**
 * @typedef {Object} LiveTVVideoPlayerConfig
 * @property {HTMLElement} container
 * @property {HTMLVideoElement} videoElement
 * @property {string} hlsUrl
 * @property {string} [dashUrl]
 * @property {boolean} autoplay
 * @property {boolean} muted
 */

class LiveTVVideoPlayer {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    this.videoElement = container.querySelector("video");
    this.hlsUrl = container.dataset.hlsUrl;
    this.dashUrl = container.dataset.dashUrl;
    this.autoplay = container.dataset.autoplay === "true";
    this.muted = container.dataset.muted === "true";
    /** @type {Hls | null} */
    this.hlsInstance = null;
    this.retryCount = 0;
    this.maxRetries = 3; // Reduced from 5 - faster initial connection
    this.retryDelay = 1500; // Reduced from 2000ms - faster retries
    /** @type {number | null} */
    this.reconnectTimer = null;
    this.isInitialized = false;
    this.isDestroyed = false;
    this.errorCount = 0;
    this.maxErrorCount = 10;
    this.lastErrorTime = null;
    this.demuxerErrorCount = 0;
    this.maxDemuxerRetries = 3;
    this.streamValidationDelay = 1000; // Wait 1 second before validating stream
    this.isLoading = false; // Prevent multiple simultaneous load attempts
    this.lastRetryTime = 0; // Track last retry time for throttling
    this.minRetryInterval = 2000; // Minimum 2 seconds between retries
    this.maxTotalRetries = 5; // Reduced from 10 - Maximum total retries across all methods
    this.totalRetryCount = 0; // Track total retries
    this.cooldownUntil = 0; // Cooldown period end time
    this.cooldownDuration = 3000; // Reduced from 5s - 3 seconds cooldown after max retries
    /** @type {AbortController | null} */
    this.abortController = null; // For canceling fetch requests
    this.isPlaying = false; // Track if video is currently playing
    this.userPaused = false; // Track if pause was user-initiated (don't auto-resume)
    this.wasPlayingWhenHidden = false; // Playback state when tab was hidden
    this.currentSrc = null; // Track current source to prevent clearing active stream
    this.isInitialLoad = true; // Track if we're in initial load phase
    this.initialLoadStartTime = 0; // Track when initial load started
    this.initialLoadGracePeriod = 10000; // 10 seconds grace period for initial load errors
    this.bufferingLogCount = 0; // Track buffering logs to reduce noise
    this.isRestoringSrc = false; // Prevent error loop when restoring src
    this.errorHandlerDisabled = false; // Temporarily disable error handler during operations

    // Bind methods to preserve context
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
  }

  init() {
    if (this.isInitialized || this.isDestroyed) {
      console.warn("[LiveTV] Player already initialized or destroyed");
      return;
    }

    if (!this.videoElement || !this.hlsUrl) {
      console.warn("[LiveTV] Video element or HLS URL not found");
      return;
    }

    this.isInitialized = true;

    // Set video attributes
    this.videoElement.muted = this.muted;
    if (!this.autoplay) {
      this.videoElement.removeAttribute("autoplay");
    }

    // Add event listeners for network and visibility changes
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    // Add video error handlers
    this.videoElement.addEventListener(
      "error",
      this.handleVideoError.bind(this)
    );
    this.videoElement.addEventListener(
      "stalled",
      this.handleVideoStalled.bind(this)
    );
    this.videoElement.addEventListener(
      "waiting",
      this.handleVideoWaiting.bind(this)
    );

    // Track playback state so auto-recovery never fights an explicit user pause
    this.videoElement.addEventListener("play", () => {
      this.userPaused = false;
      this.isPlaying = true;
    });
    this.videoElement.addEventListener("pause", () => {
      this.isPlaying = false;
      // Pauses that happen while the tab is hidden are browser throttling,
      // not user intent — don't treat them as a user pause.
      if (!document.hidden) {
        this.userPaused = true;
      }
    });

    // Wait for HLS.js to be available if needed
    this.waitForHlsJsAndInit();
  }

  /**
   * Whether the browser has real MSE support suitable for HLS.js.
   * video.canPlayType('application/vnd.apple.mpegurl') alone is not reliable:
   * some Chromium builds on Windows report "maybe"/"probably" even though the
   * browser cannot actually demux the HLS stream, which surfaces as
   * PipelineStatus::DEMUXER_ERROR_COULD_NOT_PARSE. MediaSource.isTypeSupported
   * reflects genuine playback capability, so it takes priority.
   * @returns {boolean}
   */
  supportsMSE() {
    return (
      typeof window.MediaSource !== "undefined" &&
      typeof MediaSource.isTypeSupported === "function" &&
      MediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"')
    );
  }

  /**
   * Only genuine native-HLS browsers (e.g. Safari/iOS, which lack usable MSE
   * for HLS) should use the native <video> path. Everywhere MSE is available,
   * HLS.js is more reliable.
   * @returns {boolean}
   */
  shouldUseNativeHls() {
    return Boolean(
      !this.supportsMSE() &&
        this.videoElement &&
        this.videoElement.canPlayType("application/vnd.apple.mpegurl")
    );
  }

  waitForHlsJsAndInit() {
    // Only use the native <video> path on browsers without real MSE support.
    if (this.shouldUseNativeHls()) {
      this.loadNativeHLS();
      return;
    }

    // Check if HLS.js is already loaded
    if (
      window.Hls &&
      typeof window.Hls.isSupported === "function" &&
      window.Hls.isSupported()
    ) {
      this.loadHlsJs();
      return;
    }

    // Wait for HLS.js to load (with timeout)
    const maxWaitTime = 10000; // 10 seconds
    const startTime = Date.now();
    const checkInterval = 100; // Check every 100ms

    const checkHlsJs = setInterval(() => {
      if (
        window.Hls &&
        typeof window.Hls.isSupported === "function" &&
        window.Hls.isSupported()
      ) {
        clearInterval(checkHlsJs);
        console.log("[LiveTV] HLS.js loaded, initializing player");
        this.loadHlsJs();
      } else if (Date.now() - startTime > maxWaitTime) {
        clearInterval(checkHlsJs);
        if (
          this.videoElement &&
          this.videoElement.canPlayType("application/vnd.apple.mpegurl")
        ) {
          console.warn(
            "[LiveTV] HLS.js not loaded after timeout, using native HLS fallback"
          );
          this.loadNativeHLS();
        } else {
          console.warn(
            "[LiveTV] HLS.js not loaded after timeout, using fallback"
          );
          this.loadFallback();
        }
      }
    }, checkInterval);
  }

  /**
   * Cancel any pending requests
   */
  cancelPendingRequests() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      console.log("[LiveTV] Cancelled pending requests");
    }
  }

  /**
   * Validate HLS stream URL before loading
   * @param {string} url
   * @returns {Promise<boolean>}
   */
  async validateStreamUrl(url) {
    // Cancel any previous validation requests
    this.cancelPendingRequests();

    // Create new abort controller for this request
    this.abortController = new AbortController();

    try {
      const response = await fetch(url, {
        method: "HEAD",
        mode: "no-cors", // Use no-cors to avoid CORS issues, but we can't read response
        signal: this.abortController.signal,
      });
      return true;
    } catch (error) {
      // Check if request was aborted
      if (error.name === "AbortError") {
        console.log("[LiveTV] Stream validation cancelled");
        return false;
      }

      // Try with GET request to manifest
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/vnd.apple.mpegurl, application/x-mpegURL, */*",
          },
          signal: this.abortController.signal,
        });
        if (response.ok) {
          const text = await response.text();
          // Check if it's a valid HLS manifest
          return text.includes("#EXTM3U") || text.includes("#EXT-X");
        }
      } catch (fetchError) {
        if (fetchError.name === "AbortError") {
          console.log("[LiveTV] Stream validation cancelled");
          return false;
        }
        console.warn("[LiveTV] Stream validation failed:", fetchError);
      }
      return false;
    } finally {
      this.abortController = null;
    }
  }

  loadNativeHLS() {
    if (!this.videoElement || !this.hlsUrl) {
      console.error(
        "[LiveTV] Cannot load native HLS: missing video element or URL"
      );
      return;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoading) {
      console.warn("[LiveTV] Already loading, skipping duplicate request");
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now < this.cooldownUntil) {
      const remaining = Math.ceil((this.cooldownUntil - now) / 1000);
      console.warn(
        `[LiveTV] In cooldown period, ${remaining}s remaining. Skipping load.`
      );
      return;
    }

    // Check throttling - ensure minimum time between retries
    if (now - this.lastRetryTime < this.minRetryInterval) {
      const waitTime = this.minRetryInterval - (now - this.lastRetryTime);
      console.log(`[LiveTV] Throttling: waiting ${waitTime}ms before retry`);
      setTimeout(() => this.loadNativeHLS(), waitTime);
      return;
    }

    // Check total retry limit
    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = now + this.cooldownDuration;
      this.totalRetryCount = 0; // Reset after cooldown
      return;
    }

    this.isLoading = true;
    this.lastRetryTime = now;
    this.isInitialLoad = true;
    this.initialLoadStartTime = now;
    console.log("[LiveTV] Using native HLS support");

    const loadVideo = async () => {
      if (!this.videoElement || !this.hlsUrl) {
        this.isLoading = false;
        return;
      }

      // Only clear source if it's different or video is not playing
      const needsClear =
        !this.videoElement.src ||
        this.videoElement.src !== this.hlsUrl ||
        (!this.isPlaying && this.videoElement.readyState === 0);

      if (needsClear) {
        // Clear any existing source first
        this.videoElement.src = "";
        this.videoElement.load();
        // Reduced wait time for faster loading
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      // Set the source
      this.videoElement.src = this.hlsUrl;
      this.currentSrc = this.hlsUrl;

      const onLoadedMetadata = () => {
        if (!this.videoElement) {
          this.isLoading = false;
          return;
        }
        console.log("[LiveTV] Native HLS loaded successfully");
        this.videoElement.removeEventListener(
          "loadedmetadata",
          onLoadedMetadata
        );
        this.videoElement.removeEventListener("error", onError);

        // Reset counters on success
        this.retryCount = 0;
        this.demuxerErrorCount = 0;
        this.totalRetryCount = 0;
        this.isLoading = false;
        this.isPlaying = false; // Will be set to true when play() succeeds
        this.isInitialLoad = false; // Initial load complete
        this.bufferingLogCount = 0; // Reset buffering log count

        if (this.autoplay && this.videoElement) {
          this.videoElement
            .play()
            .then(() => {
              this.isPlaying = true;
              this.isInitialLoad = false; // Mark as not initial load once playing
            })
            .catch((/** @type {unknown} */ error) => {
              console.warn("[LiveTV] Autoplay failed:", error);
              this.isPlaying = false;
            });
        }
      };

      const onError = (/** @type {Event} */ error) => {
        if (!this.videoElement) {
          this.isLoading = false;
          return;
        }
        console.error("[LiveTV] Native HLS load error:", error);

        const errorCode = this.videoElement.error?.code;
        const errorMessage = this.videoElement.error?.message || "";

        this.videoElement.removeEventListener(
          "loadedmetadata",
          onLoadedMetadata
        );
        this.videoElement.removeEventListener("error", onError);

        // Increment total retry count
        this.totalRetryCount++;
        this.isLoading = false;

        // Check for demuxer/parse errors (error code 4)
        const isDemuxerError =
          errorCode === 4 ||
          errorMessage.includes("DEMUXER_ERROR") ||
          errorMessage.includes("COULD_NOT_PARSE");

        if (isDemuxerError) {
          this.demuxerErrorCount++;
          console.warn(
            `[LiveTV] Demuxer error detected (${this.demuxerErrorCount}/${this.maxDemuxerRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
          );

          if (
            this.demuxerErrorCount < this.maxDemuxerRetries &&
            this.totalRetryCount < this.maxTotalRetries
          ) {
            // Clear source and wait longer before retry for demuxer errors
            this.videoElement.src = "";
            this.videoElement.load();
            const retryDelay = Math.min(
              this.retryDelay * (this.demuxerErrorCount + 1) * 2,
              10000
            ); // Max 10 seconds delay
            console.log(
              `[LiveTV] Retrying after ${retryDelay}ms to allow stream to stabilize`
            );
            setTimeout(() => {
              if (!this.isDestroyed) {
                this.loadNativeHLS();
              }
            }, retryDelay);
          } else {
            console.error(
              "[LiveTV] Max demuxer retries reached, trying fallback"
            );
            this.demuxerErrorCount = 0;
            this.loadFallback();
          }
        } else if (
          this.retryCount < this.maxRetries &&
          this.totalRetryCount < this.maxTotalRetries
        ) {
          this.retryCount++;
          const retryDelay = Math.min(this.retryDelay * this.retryCount, 5000); // Max 5 seconds delay
          console.log(
            `[LiveTV] Retrying native HLS load (${this.retryCount}/${this.maxRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
          );
          setTimeout(() => {
            if (!this.isDestroyed) {
              this.loadNativeHLS();
            }
          }, retryDelay);
        } else {
          console.error("[LiveTV] Max retries reached, trying fallback");
          this.loadFallback();
        }
      };

      this.videoElement.addEventListener("loadedmetadata", onLoadedMetadata, {
        once: true,
      });
      this.videoElement.addEventListener("error", onError, { once: true });
    };

    loadVideo();
  }

  async loadHlsJs() {
    if (this.isDestroyed || !this.videoElement || !this.hlsUrl) {
      console.error("[LiveTV] Cannot load HLS.js: missing requirements");
      return;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoading) {
      console.warn(
        "[LiveTV] Already loading HLS.js, skipping duplicate request"
      );
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now < this.cooldownUntil) {
      const remaining = Math.ceil((this.cooldownUntil - now) / 1000);
      console.warn(
        `[LiveTV] In cooldown period, ${remaining}s remaining. Skipping HLS.js load.`
      );
      return;
    }

    // Check throttling
    if (now - this.lastRetryTime < this.minRetryInterval) {
      const waitTime = this.minRetryInterval - (now - this.lastRetryTime);
      console.log(
        `[LiveTV] Throttling HLS.js: waiting ${waitTime}ms before retry`
      );
      setTimeout(() => this.loadHlsJs(), waitTime);
      return;
    }

    // Check total retry limit
    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = now + this.cooldownDuration;
      this.totalRetryCount = 0;
      return;
    }

    if (
      !window.Hls ||
      typeof window.Hls.isSupported !== "function" ||
      !window.Hls.isSupported()
    ) {
      console.warn("[LiveTV] HLS.js not supported, using fallback");
      this.loadFallback();
      return;
    }

    this.isLoading = true;
    this.lastRetryTime = now;
    this.isInitialLoad = true;
    this.initialLoadStartTime = now;
    console.log("[LiveTV] Using HLS.js");

    // Destroy existing instance if any
    if (this.hlsInstance) {
      this.destroyHlsInstance();
    }

    // Only clear video source if needed (not playing or different source)
    if (this.videoElement) {
      const needsClear =
        !this.videoElement.src ||
        this.videoElement.src !== this.hlsUrl ||
        (!this.isPlaying && this.videoElement.readyState === 0);

      if (needsClear) {
        this.videoElement.src = "";
        this.videoElement.load();
        // Reduced wait time for faster loading
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

    try {
      /** @type {Hls} */
      const HlsClass = window.Hls;
      this.hlsInstance = new HlsClass({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        // IW-LIVETV-05: do not climb to 1080p/3.5Mbps for a ~382x215 mobile box.
        capLevelToPlayerSize: true,
        capLevelOnFPSDrop: true,
        startLevel: -1,
        abrEwmaDefaultEstimate: 800000,
        maxBufferLength: 12,
        maxMaxBufferLength: 24,
        maxBufferSize: 15 * 1000 * 1000,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.1,
        nudgeMaxRetry: 5,
        maxFragLookUpTolerance: 0.25,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: Infinity,
        liveDurationInfinity: true,
        liveBackBufferLength: 0,
        maxLiveSyncPlaybackRate: 1,
        fragLoadingTimeOut: 20000,
        manifestLoadingTimeOut: 15000, // Increased timeout
        manifestLoadingMaxRetry: 6, // Increased retries
        manifestLoadingRetryDelay: 2000, // Longer delay between retries
        levelLoadingTimeOut: 15000,
        levelLoadingMaxRetry: 6,
        levelLoadingRetryDelay: 2000,
      });

      // Set up event listeners
      this.setupHlsEventListeners();

      // Load the source
      if (this.hlsInstance && this.hlsUrl && this.videoElement) {
        // Ensure video element doesn't have src set when HLS.js is managing it
        // HLS.js will manage the source internally
        if (this.videoElement.src && this.videoElement.src !== this.hlsUrl) {
          this.errorHandlerDisabled = true;
          this.videoElement.src = "";
          this.videoElement.load();
          setTimeout(() => {
            this.errorHandlerDisabled = false;
          }, 200);
        }

        // Attach media first
        this.hlsInstance.attachMedia(this.videoElement);
        // Then load source
        this.hlsInstance.loadSource(this.hlsUrl);
        this.currentSrc = this.hlsUrl;
      }
    } catch (/** @type {unknown} */ error) {
      console.error("[LiveTV] Error creating HLS instance:", error);
      this.isLoading = false;
      this.loadFallback();
    }
  }

  setupHlsEventListeners() {
    if (!this.hlsInstance || !window.Hls) return;

    const HlsEvents = window.Hls.Events;

    // Manifest parsed successfully
    this.hlsInstance.on(HlsEvents.MANIFEST_PARSED, () => {
      console.log("[LiveTV] HLS.js manifest parsed successfully");
      // Reset all counters on success
      this.retryCount = 0;
      this.errorCount = 0;
      this.demuxerErrorCount = 0;
      this.totalRetryCount = 0;
      this.isLoading = false;
      this.cooldownUntil = 0;
      this.isPlaying = false; // Will be set to true when play() succeeds
      this.isInitialLoad = false; // Initial load complete
      this.bufferingLogCount = 0; // Reset buffering log count

      // Reduced wait time for faster playback
      setTimeout(() => {
        if (this.autoplay && this.videoElement && this.videoElement.paused) {
          this.videoElement
            .play()
            .then(() => {
              this.isPlaying = true;
              this.isInitialLoad = false; // Mark as not initial load once playing
            })
            .catch((/** @type {unknown} */ error) => {
              console.warn("[LiveTV] Autoplay failed:", error);
              this.isPlaying = false;
            });
        }
      }, 200); // Reduced from 500ms to 200ms
    });

    // Error handling
    this.hlsInstance.on(
      HlsEvents.ERROR,
      (/** @type {string} */ event, /** @type {HlsErrorData} */ data) => {
        this.handleHlsError(data);
      }
    );

    // Level switched (quality change)
    this.hlsInstance.on(
      HlsEvents.LEVEL_SWITCHED,
      (/** @type {string} */ event, /** @type {unknown} */ data) => {
        const levelData = /** @type {{ level?: number }} */ (data);
        if (levelData.level !== undefined) {
          console.log("[LiveTV] Quality level switched:", levelData.level);
        }
      }
    );

    // Fragment loaded
    this.hlsInstance.on(HlsEvents.FRAG_LOADED, () => {
      // Reset error count on successful fragment load
      if (this.errorCount > 0) {
        this.errorCount = Math.max(0, this.errorCount - 1);
      }
    });
  }

  /**
   * @param {HlsErrorData} data
   */
  handleHlsError(data) {
    if (!data) return;

    const errorType = data.type;
    const errorDetails = data.details;
    const isFatal = data.fatal;

    console.error("[LiveTV] HLS.js error:", {
      type: errorType,
      details: errorDetails,
      fatal: isFatal,
      error: data.error,
    });

    if (!isFatal) {
      // Non-fatal errors, just log
      return;
    }

    if (!window.Hls) return;

    const HlsErrorTypes = window.Hls.ErrorTypes;

    // Handle fatal errors
    switch (errorType) {
      case HlsErrorTypes.NETWORK_ERROR:
        console.log("[LiveTV] Fatal network error, attempting recovery");
        this.errorCount++;
        this.totalRetryCount++;

        // Check total retry limit
        if (this.totalRetryCount >= this.maxTotalRetries) {
          console.error(
            `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
          );
          this.cooldownUntil = Date.now() + this.cooldownDuration;
          this.totalRetryCount = 0;
          this.loadFallback();
          break;
        }

        if (this.errorCount < this.maxErrorCount) {
          // Try to recover with throttling
          const now = Date.now();
          if (now - this.lastRetryTime >= this.minRetryInterval) {
            try {
              if (this.hlsInstance) {
                this.lastRetryTime = now;
                this.hlsInstance.startLoad();
              }
            } catch (error) {
              console.error("[LiveTV] Recovery attempt failed:", error);
              this.reconnect();
            }
          } else {
            const waitTime = this.minRetryInterval - (now - this.lastRetryTime);
            console.log(`[LiveTV] Throttling recovery: waiting ${waitTime}ms`);
            setTimeout(() => {
              if (this.hlsInstance && !this.isDestroyed) {
                try {
                  this.hlsInstance.startLoad();
                } catch (error) {
                  console.error("[LiveTV] Recovery attempt failed:", error);
                  this.reconnect();
                }
              }
            }, waitTime);
          }
        } else {
          console.error("[LiveTV] Too many network errors, reconnecting");
          this.reconnect();
        }
        break;

      case HlsErrorTypes.MEDIA_ERROR:
        console.log("[LiveTV] Fatal media error, attempting recovery");
        const errorDetails = data.details || "";
        const isDemuxerError =
          errorDetails.includes("demuxer") ||
          errorDetails.includes("parse") ||
          errorDetails.includes("DEMUXER_ERROR") ||
          errorDetails.includes("COULD_NOT_PARSE");

        if (isDemuxerError) {
          // Check if we're in initial load grace period
          const timeSinceInitialLoad = Date.now() - this.initialLoadStartTime;
          const inGracePeriod =
            this.isInitialLoad &&
            timeSinceInitialLoad < this.initialLoadGracePeriod;

          if (inGracePeriod) {
            console.log(
              `[LiveTV] Demuxer error during initial load (grace period: ${Math.ceil(
                (this.initialLoadGracePeriod - timeSinceInitialLoad) / 1000
              )}s remaining). This is normal, attempting recovery...`
            );
            // During grace period, try recovery first before destroying
            try {
              if (this.hlsInstance) {
                // Try to recover media error first
                this.hlsInstance.recoverMediaError();
                // Also try to restart load after a delay
                setTimeout(() => {
                  if (this.hlsInstance && !this.isDestroyed) {
                    try {
                      this.hlsInstance.startLoad();
                    } catch (error) {
                      // Ignore errors during recovery attempts
                    }
                  }
                }, 1500);
              }
            } catch (error) {
              // Silently ignore recovery errors during grace period
            }
            break; // Don't count this as a retry during grace period
          }

          this.demuxerErrorCount++;
          this.totalRetryCount++;
          console.warn(
            `[LiveTV] Demuxer error in HLS.js (${this.demuxerErrorCount}/${this.maxDemuxerRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
          );

          // Check total retry limit
          if (this.totalRetryCount >= this.maxTotalRetries) {
            console.error(
              `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
            );
            this.cooldownUntil = Date.now() + this.cooldownDuration;
            this.totalRetryCount = 0;
            this.demuxerErrorCount = 0;
            this.loadFallback();
            break;
          }

          if (this.demuxerErrorCount < this.maxDemuxerRetries) {
            // Try recovery first before destroying
            try {
              if (this.hlsInstance) {
                this.hlsInstance.recoverMediaError();
                // Wait a bit and restart load
                setTimeout(() => {
                  if (this.hlsInstance && !this.isDestroyed) {
                    this.hlsInstance.startLoad();
                  }
                }, 1500);
                break; // Don't destroy if recovery works
              }
            } catch (error) {
              console.warn(
                "[LiveTV] Recovery attempt failed, will recreate instance:",
                error
              );
            }

            // If recovery didn't work, destroy and recreate
            this.destroyHlsInstance();
            const retryDelay = Math.min(
              this.retryDelay * (this.demuxerErrorCount + 1),
              5000
            ); // Reduced max delay
            console.log(`[LiveTV] Retrying HLS.js after ${retryDelay}ms`);
            setTimeout(() => {
              if (!this.isDestroyed) {
                this.loadHlsJs();
              }
            }, retryDelay);
          } else {
            console.error(
              "[LiveTV] Max demuxer retries reached, trying fallback"
            );
            this.demuxerErrorCount = 0;
            this.reconnect();
          }
        } else {
          // Regular media error recovery
          try {
            if (this.hlsInstance) {
              this.hlsInstance.recoverMediaError();
            }
          } catch (error) {
            console.error("[LiveTV] Media error recovery failed:", error);
            this.reconnect();
          }
        }
        break;

      default:
        console.error("[LiveTV] Unrecoverable error, reconnecting");
        this.reconnect();
        break;
    }
  }

  reconnect() {
    if (this.isDestroyed || !this.videoElement) return;

    // Prevent multiple simultaneous reconnection attempts
    if (this.isLoading) {
      console.warn("[LiveTV] Already reconnecting, skipping duplicate request");
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now < this.cooldownUntil) {
      const remaining = Math.ceil((this.cooldownUntil - now) / 1000);
      console.warn(
        `[LiveTV] In cooldown period, ${remaining}s remaining. Skipping reconnect.`
      );
      return;
    }

    // Check throttling
    if (now - this.lastRetryTime < this.minRetryInterval) {
      const waitTime = this.minRetryInterval - (now - this.lastRetryTime);
      console.log(`[LiveTV] Throttling reconnect: waiting ${waitTime}ms`);
      setTimeout(() => this.reconnect(), waitTime);
      return;
    }

    // Clear any existing reconnect timer
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
    }

    // Check total retry limit
    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = now + this.cooldownDuration;
      this.totalRetryCount = 0;
      this.loadFallback();
      return;
    }

    if (this.retryCount >= this.maxRetries) {
      console.error("[LiveTV] Max retries reached, trying fallback");
      this.loadFallback();
      return;
    }

    this.retryCount++;
    this.totalRetryCount++;
    const delay = Math.min(
      this.retryDelay * Math.min(this.retryCount, 5),
      10000
    ); // Max 10 seconds

    console.log(
      `[LiveTV] Reconnecting in ${delay}ms (attempt ${this.retryCount}/${this.maxRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
    );

    this.reconnectTimer = setTimeout(() => {
      if (this.isDestroyed || !this.videoElement) return;

      // Destroy current instance
      this.destroyHlsInstance();

      // Reset demuxer error count on reconnection attempt
      this.demuxerErrorCount = 0;

      // Reinitialize
      if (this.shouldUseNativeHls()) {
        this.loadNativeHLS();
      } else if (
        window.Hls &&
        typeof window.Hls.isSupported === "function" &&
        window.Hls.isSupported()
      ) {
        this.loadHlsJs();
      } else {
        this.loadFallback();
      }
    }, delay);
  }

  /**
   * @param {Event} event
   */
  handleVideoError(event) {
    // Prevent handling errors during loading or when restoring src
    if (this.isLoading || this.isRestoringSrc || this.errorHandlerDisabled) {
      if (this.isRestoringSrc) {
        // Silently ignore errors during src restoration
        return;
      }
      console.warn(
        "[LiveTV] Error during loading, will retry after load completes"
      );
      return;
    }

    console.error("[LiveTV] Video element error:", event);

    if (this.videoElement && this.videoElement.error) {
      const errorCode = this.videoElement.error.code;
      const errorMessage = this.videoElement.error.message || "";
      console.error(
        `[LiveTV] Video error code: ${errorCode}, message: ${errorMessage}`
      );

      // Check for empty src attribute error
      if (
        errorMessage.includes("Empty src attribute") ||
        (errorCode === 4 && !this.videoElement.src)
      ) {
        // Only restore if HLS.js is not managing the video
        if (this.hlsInstance) {
          console.warn(
            "[LiveTV] Empty src detected but HLS.js is managing video, ignoring"
          );
          return;
        }

        console.warn("[LiveTV] Empty src attribute detected, restoring source");
        this.isRestoringSrc = true;
        this.errorHandlerDisabled = true;

        // Restore source if we have one
        if (this.currentSrc) {
          this.videoElement.src = this.currentSrc;
        } else if (this.hlsUrl) {
          this.videoElement.src = this.hlsUrl;
          this.currentSrc = this.hlsUrl;
        }

        // Use requestAnimationFrame to ensure src is set before load
        requestAnimationFrame(() => {
          if (this.videoElement && this.videoElement.src) {
            this.videoElement.load();
            // Re-enable error handler after a short delay
            setTimeout(() => {
              this.isRestoringSrc = false;
              this.errorHandlerDisabled = false;
            }, 500);
          } else {
            this.isRestoringSrc = false;
            this.errorHandlerDisabled = false;
          }
        });
        return;
      }

      // Check for demuxer/parse errors (error code 4 = MEDIA_ERR_SRC_NOT_SUPPORTED)
      const isDemuxerError =
        (errorCode === 4 && this.videoElement.src) || // Only if src exists
        errorMessage.includes("DEMUXER_ERROR") ||
        errorMessage.includes("COULD_NOT_PARSE") ||
        errorMessage.includes("demuxer") ||
        errorMessage.includes("parse");

      if (isDemuxerError) {
        // Check if we're in initial load grace period
        const timeSinceInitialLoad = Date.now() - this.initialLoadStartTime;
        const inGracePeriod =
          this.isInitialLoad &&
          timeSinceInitialLoad < this.initialLoadGracePeriod;

        if (inGracePeriod) {
          console.log(
            `[LiveTV] Demuxer error during initial load (grace period: ${Math.ceil(
              (this.initialLoadGracePeriod - timeSinceInitialLoad) / 1000
            )}s remaining). This is normal for live streams.`
          );
          // Don't count as retry during grace period
          // If HLS.js is managing, let it handle recovery
          if (this.hlsInstance) {
            return; // HLS.js will handle recovery
          }
          // For native HLS, wait a bit and retry
          setTimeout(() => {
            if (
              !this.isDestroyed &&
              this.videoElement &&
              this.videoElement.src === this.hlsUrl
            ) {
              // Source is correct, just wait for stream to stabilize
              return;
            }
          }, 2000);
          return; // Don't count this as a retry during grace period
        }

        this.demuxerErrorCount++;
        this.totalRetryCount++;
        console.warn(
          `[LiveTV] Demuxer error detected in video element (${this.demuxerErrorCount}/${this.maxDemuxerRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
        );

        // Check total retry limit
        if (this.totalRetryCount >= this.maxTotalRetries) {
          console.error(
            `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
          );
          this.cooldownUntil = Date.now() + this.cooldownDuration;
          this.totalRetryCount = 0;
          this.demuxerErrorCount = 0;
          this.loadFallback();
          return;
        }

        // Don't clear source if HLS.js is managing it or video is playing
        if (this.hlsInstance) {
          // HLS.js is managing, let it handle recovery
          console.log(
            "[LiveTV] HLS.js is managing video, letting it handle recovery"
          );
          if (this.demuxerErrorCount < this.maxDemuxerRetries) {
            const retryDelay = Math.min(
              this.retryDelay * (this.demuxerErrorCount + 1),
              3000
            );
            setTimeout(() => {
              if (!this.isDestroyed && this.hlsInstance) {
                try {
                  this.hlsInstance.recoverMediaError();
                  this.hlsInstance.startLoad();
                } catch (error) {
                  console.warn("[LiveTV] HLS.js recovery failed:", error);
                }
              }
            }, retryDelay);
          }
          return;
        }

        // Only clear source if video is not playing and not managed by HLS.js
        if (this.videoElement && !this.isPlaying && !this.hlsInstance) {
          this.errorHandlerDisabled = true;
          this.videoElement.src = "";
          this.videoElement.load();
          setTimeout(() => {
            this.errorHandlerDisabled = false;
          }, 300);
        }

        if (this.demuxerErrorCount < this.maxDemuxerRetries) {
          // Reduced delay for faster retry
          const retryDelay = Math.min(
            this.retryDelay * (this.demuxerErrorCount + 1),
            4000
          ); // Reduced max delay from 10s to 4s
          console.log(
            `[LiveTV] Retrying after ${retryDelay}ms to allow stream to stabilize`
          );
          setTimeout(() => {
            if (!this.isDestroyed) {
              this.reconnect();
            }
          }, retryDelay);
        } else {
          console.error(
            "[LiveTV] Max demuxer retries reached, trying fallback"
          );
          this.demuxerErrorCount = 0;
          this.loadFallback();
        }
        return;
      }
    }

    // Try to recover for other errors
    this.totalRetryCount++;
    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = Date.now() + this.cooldownDuration;
      this.totalRetryCount = 0;
      this.loadFallback();
    } else if (this.retryCount < this.maxRetries) {
      this.reconnect();
    } else {
      this.loadFallback();
    }
  }

  handleVideoStalled() {
    // Only log if not already playing to reduce noise
    if (!this.isPlaying) {
      console.warn("[LiveTV] Video stalled, attempting to resume");
    }

    // Respect an explicit user pause — never auto-resume over it
    if (this.userPaused) return;

    // Try to play if paused and source exists
    if (
      this.videoElement &&
      this.videoElement.paused &&
      this.videoElement.src
    ) {
      this.videoElement
        .play()
        .then(() => {
          this.isPlaying = true;
        })
        .catch((/** @type {Error} */ error) => {
          console.error("[LiveTV] Failed to resume playback:", error);
          this.isPlaying = false;
        });
    }
  }

  handleVideoWaiting() {
    // Video is buffering, this is normal for live streams
    // Reduce log noise - only log first few times or if there are errors
    this.bufferingLogCount++;
    if (this.bufferingLogCount <= 2 || this.errorCount > 0) {
      if (this.isInitialLoad) {
        console.log(
          "[LiveTV] Video buffering during initial load (this is normal)..."
        );
      } else if (this.bufferingLogCount <= 2) {
        console.log("[LiveTV] Video buffering...");
      }
    }
    // Reset counter after a while to allow occasional logging
    if (this.bufferingLogCount > 10) {
      this.bufferingLogCount = 0;
    }
  }

  handleOnline() {
    console.log("[LiveTV] Network connection restored");

    // Reset error count
    this.errorCount = 0;

    // Respect an explicit user pause — don't restart the stream over it
    if (this.userPaused) return;

    // Reconnect if not already playing
    if (
      this.videoElement &&
      (this.videoElement.paused || this.videoElement.readyState < 2)
    ) {
      this.reconnect();
    }
  }

  handleOffline() {
    console.warn("[LiveTV] Network connection lost");
  }

  handleVisibilityChange() {
    if (!this.videoElement) return;

    if (document.hidden) {
      // Do NOT pause the live stream when the tab is hidden (IWS-652).
      // Pausing makes the stream show a paused/poster state when the user
      // returns. Let playback continue; the browser may throttle it, but the
      // stream stays at (or near) the live edge. Just remember whether
      // playback was active so we can resume if the browser paused it.
      this.wasPlayingWhenHidden = this.isPlaying && !this.videoElement.paused;
      return;
    }

    // Tab is visible again. Never override an explicit user pause.
    if (this.userPaused) return;

    if (
      this.wasPlayingWhenHidden &&
      this.videoElement.paused &&
      (this.videoElement.src || this.hlsInstance)
    ) {
      // Browser paused playback while backgrounded; resume it.
      this.videoElement
        .play()
        .then(() => {
          this.isPlaying = true;
          console.log("[LiveTV] Tab visible, video resumed");
        })
        .catch((/** @type {unknown} */ error) => {
          console.warn("[LiveTV] Failed to resume playback:", error);
          this.isPlaying = false;
        });
    } else if (
      !this.videoElement.src &&
      !this.hlsInstance &&
      this.hlsUrl
    ) {
      // If no source but we have URL, reload
      console.log("[LiveTV] Tab visible, reloading stream");
      if (this.shouldUseNativeHls()) {
        this.loadNativeHLS();
      } else {
        this.loadHlsJs();
      }
    }
  }

  async loadFallback() {
    if (!this.videoElement) {
      console.error("[LiveTV] Cannot load fallback: missing video element");
      return;
    }

    // Prevent multiple simultaneous fallback loads
    if (this.isLoading) {
      console.warn(
        "[LiveTV] Already loading fallback, skipping duplicate request"
      );
      return;
    }

    this.isLoading = true;
    console.log("[LiveTV] Loading fallback stream");

    // Destroy HLS instance if exists
    this.destroyHlsInstance();

    // Only clear source if needed
    const needsClear =
      !this.videoElement.src ||
      (this.dashUrl && this.videoElement.src !== this.dashUrl) ||
      (this.hlsUrl && this.videoElement.src !== this.hlsUrl && !this.dashUrl) ||
      (!this.isPlaying && this.videoElement.readyState === 0);

    if (needsClear) {
      this.videoElement.src = "";
      this.videoElement.load();
      // Reduced wait time
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    // Reset error counts but keep total retry count for cooldown tracking
    this.demuxerErrorCount = 0;
    this.retryCount = 0;
    this.errorCount = 0;

    if (this.dashUrl) {
      this.videoElement.src = this.dashUrl;
      this.currentSrc = this.dashUrl;
      console.log("[LiveTV] Using DASH fallback");
    } else if (this.hlsUrl) {
      this.videoElement.src = this.hlsUrl;
      this.currentSrc = this.hlsUrl;
      console.log("[LiveTV] Using direct HLS fallback");
    } else {
      console.error("[LiveTV] No fallback URLs available");
      this.isLoading = false;
      return;
    }

    // Add error handler for fallback
    const onError = () => {
      console.error("[LiveTV] Fallback stream also failed");
      this.isLoading = false;
      if (this.videoElement) {
        this.videoElement.removeEventListener("error", onError);
      }
      // Don't retry fallback - user intervention needed
    };

    const onLoadedMetadata = () => {
      console.log("[LiveTV] Fallback stream loaded successfully");
      this.isLoading = false;
      this.isPlaying = false; // Will be set to true when play() succeeds
      // Reset counters on success
      this.totalRetryCount = 0;
      this.cooldownUntil = 0;
      if (this.videoElement) {
        this.videoElement.removeEventListener(
          "loadedmetadata",
          onLoadedMetadata
        );
        // Auto-play if enabled
        if (this.autoplay) {
          this.videoElement
            .play()
            .then(() => {
              this.isPlaying = true;
            })
            .catch((/** @type {unknown} */ error) => {
              console.warn("[LiveTV] Fallback autoplay failed:", error);
              this.isPlaying = false;
            });
        }
      }
    };

    this.videoElement.addEventListener("error", onError, { once: true });
    this.videoElement.addEventListener("loadedmetadata", onLoadedMetadata, {
      once: true,
    });
  }

  destroyHlsInstance() {
    // Cancel any pending requests
    this.cancelPendingRequests();

    if (this.hlsInstance) {
      try {
        // Destroy HLS instance (this will stop loading automatically)
        this.hlsInstance.destroy();
      } catch (error) {
        console.error("[LiveTV] Error destroying HLS instance:", error);
      }
      this.hlsInstance = null;
    }
  }

  destroy() {
    if (this.isDestroyed) return;

    this.isDestroyed = true;
    this.isInitialized = false;
    this.isLoading = false;

    // Cancel any pending requests
    this.cancelPendingRequests();

    // Clear reconnect timer
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Remove event listeners
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );

    // Destroy HLS instance
    this.destroyHlsInstance();

    // Pause and clear video source
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.src = "";
      this.videoElement.load();
    }

    console.log("[LiveTV] Player destroyed");
  }
}

// Initialize all Live TV video players on the page
function initializeLiveTVPlayers() {
  const containers = document.querySelectorAll("[data-live-tv-player]");

  containers.forEach((/** @type {HTMLElement} */ container) => {
    // Skip if already initialized
    if (container.__liveTvPlayer) {
      return;
    }

    const player = new LiveTVVideoPlayer(container);
    player.init();

    // Store instance for potential cleanup
    container.__liveTvPlayer = player;
  });

  console.log(`[LiveTV] Initialized ${containers.length} video player(s)`);
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeLiveTVPlayers);
} else {
  // DOM is already ready
  initializeLiveTVPlayers();
}

// Also initialize after a short delay to catch late-loading elements
setTimeout(initializeLiveTVPlayers, 1000);

// Cleanup on page unload
window.addEventListener("beforeunload", () => {
  const containers = document.querySelectorAll("[data-live-tv-player]");
  containers.forEach((/** @type {HTMLElement} */ container) => {
    if (container.__liveTvPlayer) {
      container.__liveTvPlayer.destroy();
    }
  });
});

// Export for potential external use
if (typeof module !== "undefined" && module.exports) {
  module.exports = LiveTVVideoPlayer;
}
